import { createContext, useContext } from 'react';

/**
 * WEB: «Экран после оплаты» (isPostPurchase) — данные доступные блокам этого
 * экрана. Сейчас единственный потребитель — return_to_app_button (нужен
 * appSchemeUrl для возврата в приложение по кастомной URL-схеме, WEB-800).
 *
 * Дефолт — пустой объект: канвас редактора и любой рендер без провайдера
 * получают appSchemeUrl=undefined (кнопка всё равно видна, просто без href).
 */
export interface PostPurchaseContextValue {
  appSchemeUrl?: string;
  /**
   * WEB-854 «приложение уже установлено»: запасной путь, если по схеме
   * приложение не открылось. install-ссылки (WEB-433) несут метку перехода и
   * идут ПЕРВЫМИ; голые ссылки магазинов проекта — фолбэк.
   */
  installUrlIos?: string | null;
  installUrlAndroid?: string | null;
  iosStoreUrl?: string | null;
  androidStoreUrl?: string | null;
}

const DEFAULT_VALUE: PostPurchaseContextValue = {};

/**
 * WEB-849: правдоподобные тестовые данные покупки для предпросмотра «как после
 * оплаты» в конструкторе. Пустой контекст (как было) означал «покупка не
 * найдена» — кнопка возврата рисовалась без адреса, и автор не мог проверить
 * ни её вид, ни то, что она вообще появится. Значения намеренно узнаваемо
 * тестовые (example-адреса магазинов): в превью по ним всё равно никто не
 * переходит — клик у кнопки перехвачен превью-гардом (WEB-846).
 *
 * СХЕМА ПРИЛОЖЕНИЯ — НАСТОЯЩАЯ, А НЕ ВЫДУМАННАЯ (ревизия пачки 19). Здесь
 * стояла константа `myapp://purchase/success?session_id=cs_test_preview`, и
 * после WEB-1911 она стала не декорацией, а ПРАВИЛОМ: своя ссылка автора
 * проходит дверь `appLinkHref` только при совпадении схем. У проекта со своей
 * схемой (`somatica`) канва отвергала правильную ссылку `somatica://close` и
 * принимала нерабочую `myapp://close` — врала в обе стороны. Поэтому схему
 * даёт вызывающий, а берёт он её из настроек проекта
 * (`bridgeConfig.returnScheme`, WEB-800/803).
 *
 * Схема у проекта не настроена → `appSchemeUrl` НЕТ вовсе — ровно как
 * публично: сервер в этом случае поля не отдаёт (handoff-success,
 * resolveAppSchemeUrl), и кнопка живёт без адреса.
 *
 * Форма адреса зеркалит серверную (`<scheme>://handoff?code=<code>`,
 * HandoffHelperService.buildAppSchemeUrl) — с узнаваемо тестовым кодом.
 */
export function buildPreviewPostPurchaseData(
  returnScheme?: string | null,
): PostPurchaseContextValue {
  const scheme = (returnScheme ?? '').trim();
  return {
    ...(scheme ? { appSchemeUrl: `${scheme}://handoff?code=preview` } : {}),
    installUrlIos: null,
    installUrlAndroid: null,
    iosStoreUrl: 'https://apps.apple.com/app/id000000000',
    androidStoreUrl: 'https://play.google.com/store/apps/details?id=com.example.app',
  };
}

export const PostPurchaseContext = createContext<PostPurchaseContextValue>(DEFAULT_VALUE);

export function usePostPurchaseContext(): PostPurchaseContextValue {
  return useContext(PostPurchaseContext);
}
